import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client/http-client';
import { queryKeys } from '../utils/query-keys';

export function useEquipment(params?: any) {
  return useQuery({
    queryKey: queryKeys.equipment.list(params),
    queryFn: () => apiClient.getEquipment(params),
    staleTime: 10 * 60 * 1000, // 10 minutes - equipment changes less frequently
  });
}

export function useEquipmentItem(id: string) {
  return useQuery({
    queryKey: queryKeys.equipment.detail(id),
    queryFn: () => apiClient.getEquipmentItem(id),
    enabled: !!id,
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
}

// Hook for featured equipment
export function useFeaturedEquipment() {
  return useEquipment({
    featured: true,
    limit: 8,
  });
}

// Hook for equipment by category
export function useEquipmentByCategory(category: string) {
  return useEquipment({
    category,
    limit: 20,
  });
}

export function useLikeEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.likeEquipment(id),
    onSuccess: (_, equipmentId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(equipmentId),
      });
    },
  });
}

export function useUnlikeEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.unlikeEquipment(id),
    onSuccess: (_, equipmentId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(equipmentId),
      });
    },
  });
}

export function useBookmarkEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.bookmarkEquipment(id),
    onSuccess: (_, equipmentId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(equipmentId),
      });
    },
  });
}

export function useUnbookmarkEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.unbookmarkEquipment(id),
    onSuccess: (_, equipmentId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(equipmentId),
      });
    },
  });
}

export function useEquipmentInteractions(equipmentId: string) {
  const likeEquipment = useLikeEquipment();
  const unlikeEquipment = useUnlikeEquipment();
  const bookmarkEquipment = useBookmarkEquipment();
  const unbookmarkEquipment = useUnbookmarkEquipment();

  return {
    like: () => likeEquipment.mutate(equipmentId),
    unlike: () => unlikeEquipment.mutate(equipmentId),
    bookmark: () => bookmarkEquipment.mutate(equipmentId),
    unbookmark: () => unbookmarkEquipment.mutate(equipmentId),
    isLiking: likeEquipment.isPending,
    isUnliking: unlikeEquipment.isPending,
    isBookmarking: bookmarkEquipment.isPending,
    isUnbookmarking: unbookmarkEquipment.isPending,
  };
}

export function useCreateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (equipment: any) => apiClient.createEquipment(equipment),
    onSuccess: () => {
      // Invalidate equipment list queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.lists(),
      });
    },
  });
}

export function useUpdateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, equipment }: { id: string; equipment: any }) =>
      apiClient.updateEquipment(id, equipment),
    onSuccess: (_, { id }) => {
      // Invalidate specific equipment detail and list queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.lists(),
      });
    },
  });
}
